import React, { useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import { postCustomer } from "../../../../../shared/services/services";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import PopupModules from "src/features/auth/components/PopupModules";
import InputGroup from "./InputGroup";
import { useUser } from "src/shared/context/UserContext";
import { textColorOptions } from "src/shared/modules/profile/theme/themeConstants";
import { errorHandling } from "src/shared/services/errorHandling";
import { Plus, Trash2 } from "lucide-react";

type Props = {
  close: (data: any) => void;
  loading: (data: boolean) => void;
  addCustomer: boolean;
};

const AddCustomer: React.FC<Props> = ({ close, loading, addCustomer }) => {
  const { user } = useUser();

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phonesNumbers: [""],
    emailIds: [""],
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    openingOutstandingBalance: ""
  });

  const [showMore, setShowMore] = useState(false);
  const [showMiddleName, setShowMiddleName] = useState(false);

  // ================= CHANGE HANDLERS =================
  const handleChange = (name: string, value: string) => {
    if (name === "openingOutstandingBalance") {
      const balance = Math.max(0, parseFloat(value) || 0);
      setFormData((prev) => ({ ...prev, openingOutstandingBalance: String(balance) }));
      return;
    }
    if (name === "middleName") setShowMiddleName(true);
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "middleName") {
      setShowMiddleName(true);
    }
  };

  const handleArrayChange = (
    index: number,
    value: string,
    field: "phonesNumbers" | "emailIds"
  ) => {
    // limit max length to 12 only for phone numbers
    const updatedValue =
      field === "phonesNumbers" ? value.slice(0, 12) : value;

    const updated = [...formData[field]];
    updated[index] = updatedValue;

    setFormData((prev) => ({
      ...prev,
      [field]: updated,
    }));
  };

  const addField = (field: "phonesNumbers" | "emailIds") => {
    if (formData[field].length >= 3) {
      notifyError("Maximum 3 entries allowed");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeField = (index: number, field: "phonesNumbers" | "emailIds") => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      [field]: updated.length ? updated : [""],
    }));
  };

  const handleNumberKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["-", "e", "E", "+", ".", "(", ")"].includes(e.key)) e.preventDefault();
  };

  // ================= SUBMIT =================
  const handleConfirm = async () => {
    const validPhones = formData.phonesNumbers.filter((p) => p.trim());
    const validEmails = formData.emailIds.filter((e) => e.trim());

    if (!formData.firstName.trim()) {
      notifyError("First Name is required");
      return;
    }

    if (!validPhones.length) {
      notifyError("At least one phone number is required");
      return;
    }

    const payload = {
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim(),
      lastName: formData.lastName.trim(),
      phones: validPhones,
      emails: validEmails,
      addresses: {
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
      },
      openingOutstandingBalance: Number(formData.openingOutstandingBalance)
    };

    try {
      loading(true);
      const res = await postCustomer(payload);
      notifySuccess("Customer added successfully!");
      Clearform();
      close(res);
    } catch (error: any) {
      errorHandling(error);
    } finally {
      loading(false);
    }
  };

  // ================= RESET =================
  const Clearform = () => {
    setFormData({
      firstName: "",
      middleName: "",
      lastName: "",
      phonesNumbers: [""],
      emailIds: [""],
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      openingOutstandingBalance: ""
    });
    setShowMore(false);
    setShowMiddleName(false)
  };

  const btnDisabled =
    !formData.firstName.trim() ||
    !formData.phonesNumbers[0] ||
    formData.phonesNumbers[0].length < 8;

  // ================= UI =================
  return (
    <PopupModules
      isOpen={addCustomer}
      onClose={() => {
        Clearform();
        close("");
      }}
      title="Add New Customer"
    >
      <div className="flex flex-col gap-3 p-3">

        {/* NAME */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputGroup label="First Name" value={formData.firstName} name="firstName" onChange={handleChange} mandatory />
          <InputGroup label="Last Name" value={formData.lastName} name="lastName" onChange={handleChange} mandatory />
        </div>
        {!showMiddleName && <button className={`text-sm text-end font-medium mb-1 ${textColorOptions[user?.themes.backgroundColour ?? "Blue"]}`} onClick={() => setShowMiddleName(!showMiddleName)}>+ Middle Name</button>}
        {showMiddleName && <div className="flex items-center gap-2 justify-between w-full"><InputGroup label="Middle Name" value={formData.middleName} name="middleName" onChange={handleChange} /> </div>}

        {/* PHONE */}
        <div >
          <p className="text-sm font-medium mb-1">Phone Numbers <span className="text-red-500">*</span></p>
          {formData.phonesNumbers.map((phone, index) => {
            const isInvalid =
              phone.length > 0 && (phone.length < 8 || phone.length > 12);

            return (
              <div key={index} className="mb-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) =>
                      handleArrayChange(index, e.target.value, "phonesNumbers")
                    }
                    onKeyDown={handleNumberKey}
                    className={`w-full border rounded px-2 py-2 ${isInvalid ? "border-red-500" : ""
                      }`}
                    placeholder="Enter phone number"
                  />

                  {formData.phonesNumbers.length < 2 && (
                    <button onClick={() => addField("phonesNumbers")}>
                      <Plus size={18} />
                    </button>
                  )}

                  {formData.phonesNumbers.length > 1 && (
                    <button
                      onClick={() => removeField(index, "phonesNumbers")}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                {isInvalid && (
                  <p className="text-sm text-red-500 mt-1">
                    Phone number must be 8-12 digits
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* TOGGLE */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowMore(!showMore)}
            className={`text-sm ${textColorOptions[user?.themes.backgroundColour ?? "Blue"]}`}
          >
            {showMore ? "Hide Details ▲" : "More Details ▼"}
          </button>
        </div>

        {/* MORE DETAILS */}
        {showMore && (
          <div className="border-t pt-3">
            <InputGroup label="Opening OutstandingBalance" type="number" value={formData.openingOutstandingBalance ?? 0} name="openingOutstandingBalance" onChange={handleChange} />
            {/* EMAIL */}
            <div className="mt-2">
              <p className="text-sm font-medium mb-1">Email IDs</p>
              {formData.emailIds.map((email, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleArrayChange(index, e.target.value, "emailIds")}
                    className="w-full border rounded px-2 py-2"
                    placeholder="Enter email"
                  />

                  {formData.emailIds.length < 2 && (
                    <button onClick={() => addField("emailIds")}>
                      <Plus size={18} />
                    </button>
                  )}

                  {formData.emailIds.length > 1 && (
                    <button onClick={() => removeField(index, "emailIds")}>
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* ADDRESS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <InputGroup label="Address Line 1" value={formData.addressLine1} name="addressLine1" onChange={handleChange} />
              <InputGroup label="Address Line 2" value={formData.addressLine2} name="addressLine2" onChange={handleChange} />
              <InputGroup label="City" value={formData.city} name="city" onChange={handleChange} />
              <InputGroup label="State" value={formData.state} name="state" onChange={handleChange} />
              <InputGroup label="Postal Code" value={formData.postalCode} name="postalCode" onChange={handleChange} />
            </div>

          </div>
        )}

        {/* BUTTONS */}
        <div className="flex justify-center gap-4 pt-4 border-t">
          <Button
            label="Cancel"
            variant="ThemeCancel"
            onClick={() => {
              Clearform();
              close("");
            }}
          />
          <Button label="Confirm" variant="ThemeBase" onClick={handleConfirm} disabled={btnDisabled} />
        </div>

      </div>
    </PopupModules>
  );
};

export default AddCustomer;